import { browser } from "@workspace/mobile";
import { Paperclip } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  apiFetchNotOkHandler,
  errorHandler
} from "../../../../../../APIs/errorHandler";
import { Attachments, Messages } from "../../../../../../types/messages";
import { dmnIdentity } from "../../../../../../utils/dmnIdentity";
import {
  getEnvironmentProps,
  GetEnvironmentPropsResponse
} from "../../../../../../utils/environment";
import DetachedModal from "../../../../../Shared/Modals/DetachedModal/DetachedModal";

type ViewEmailModalType = {
  message: Messages;
  handleClose: () => void;
};

const ViewEmailModal = ({ message, handleClose }: ViewEmailModalType) => {
  const [token, setToken] = useState<GetSingleUseTokenType | undefined>(
    undefined
  );

  useEffect(() => {
    getToken();
  }, []);

  const getToken = async () => {
    const tokenResponse = await getSingleUseToken();
    setToken(tokenResponse);
  };

  const sentDate = new Date(message.created_date).toLocaleDateString(
    window.locale,
    {
      hour: "2-digit",
      minute: "2-digit"
    }
  );

  return (
    <DetachedModal
      heading={message.subject ?? "Email Details"}
      width="70%"
      height="90vh"
      handleClose={handleClose}
    >
      <iframe name="iframe_a" style={{ display: "none" }} />
      <div className="ViewEmailModalDetails pt-[calc(var(--panelPadding)*2)] flex flex-col flex-1">
        <div className="ViewEmailModalDetails-Heading flex justify-between">
          <div className="ViewEmailModalDetails-Left">
            <div>
              {message.from_name}
              <span className="emailAddress text-[var(--tertiaryColor)]">{` <${message.from_email}>`}</span>
            </div>
            <div className="Flex">
              To:
              {typeof message.to === "object"
                ? message.to?.map((t) => (
                    <div key={t.address} className="EmailToName pl-1">
                      {` ${t.name}`}
                      <span className="emailAddress text-[var(--tertiaryColor)]">{` <${t.address}>`}</span>
                    </div>
                  ))
                : message.to}
            </div>
            {(message.attachments ?? []).length > 0 && (
              <div>
                <Paperclip className="size-5" />
                {message.attachments?.map((attachment) => (
                  <button
                    type="button"
                    key={attachment.path}
                    className="attachment-hyperlink text-[var(--primaryColor)] pr-1"
                    onClick={() => {
                      if (token) {
                        const url = getAttachmentDownloadUrl(attachment, token);
                        browser.open(url);
                      }
                    }}
                  >
                    {attachment.filename}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="ViewEmailModalDetails-Right text-right">
            <div>Status: {message.status ?? message.action}</div>
            <div>{sentDate.split(",").join("")}</div>
          </div>
        </div>
        <div className="ViewEmailModal-MessageContainer pt-[calc(var(--panelPadding)*2)] flex-[1_100%] flex flex-col">
          <div className="ViewEmailModal-Body h-0 grow">
            <iframe
              title="Email message content"
              className="ViewEmailModal-Iframe border-none w-full h-full bg-[var(--defaultEmailBackground)]"
              src={URL.createObjectURL(
                new Blob([message.body], {
                  type: "text/html"
                })
              )}
            />
          </div>
        </div>
      </div>
    </DetachedModal>
  );
};

export default React.memo<ViewEmailModalType>(ViewEmailModal);

const getAttachmentDownloadUrl = (
  attachment: Attachments,
  props: GetSingleUseTokenType
): string => {
  const requestParams = {
    action: "downloadAttachment",
    attachment: JSON.stringify(attachment),
    single_use_token: props.token,
    source: "admin"
  };
  const searchParams = new URLSearchParams(requestParams);

  const URL = `${
    props.environmentVars?.CORE_API_BASE_URL
  }/messages?${searchParams.toString()}`;

  return URL;
};

type GetSingleUseTokenType = {
  token: string;
  environmentVars: GetEnvironmentPropsResponse | undefined;
};
const getSingleUseToken = async (): Promise<
  GetSingleUseTokenType | undefined
> => {
  const identityToken = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(identityToken);
  const url = `${environmentVars?.AUTH_API_BASE_URL}/oauth/onetime`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${identityToken}`
      }
    });
    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error fetching single use token");
      return undefined;
    }

    const token = (await response?.json())?.accessToken;

    return token ? { token, environmentVars } : undefined;
  } catch (error) {
    errorHandler(error, "Error fetching single use token");

    return undefined;
  }
};
